import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Typography,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Stack,
    Collapse,
    Alert,
    AlertTitle,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid
} from '@mui/material';
import {
    CloudUpload,
    Download,
    CheckCircle,
    Error,

    ExpandMore,
    ExpandLess,
    FilterList,
    AttachFile,
    UploadFile
} from '@mui/icons-material';
import { downloadSalaryTemplate, uploadBulkSalaryUpdate, getSalaryFilterOptions } from '../../api';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import { TABLE_PAPER_SX, HEAD_CELL_SX, TABLE_ROW_SX, BODY_CELL_SX } from '../common/tableStyles';
import { SELECTED_MENU_PROPS } from '../StyledComponents/StyledComponent';

// ---- FORTHEYE soft-card tokens (shared with the rest of the portal) ----
const CARD_SX = {
    p: { xs: 2, sm: 2.5, md: 3 },
    borderRadius: '22px',
    bgcolor: '#fff',
    border: '1px solid #eef0f4',
    boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
};
const ACCENT = '#2f6bff';
const OUTLINED_BTN = { textTransform: 'none', fontWeight: 700, borderRadius: '12px', color: '#6b7280', borderColor: '#e5e7eb' };
const GRADIENT_BTN = {
    textTransform: 'none', fontWeight: 700, borderRadius: '12px',
    background: 'linear-gradient(135deg,#3b82f6,#2f6bff)',
    '&:hover': { background: 'linear-gradient(135deg,#2f6bff,#2557d6)' },
};

// Soft tinted result tile: colored icon tile + label + number.
const ResultTile = ({ icon, label, value, color }) => (
    <Paper
        elevation={0}
        sx={{
            ...CARD_SX,
            p: { xs: 1.75, md: 2 },
            flex: '1 1 200px',
            minWidth: 180,
        }}
    >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 38, height: 38, borderRadius: '11px', bgcolor: `${color}14`, color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {React.cloneElement(icon, { sx: { fontSize: 20 } })}
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ color: '#6b7280', fontWeight: 600, fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</Typography>
                <Typography sx={{ fontWeight: 800, fontSize: '1.5rem', color, lineHeight: 1.1 }}>{value}</Typography>
            </Box>
        </Box>
    </Paper>
);

const BulkSalaryUpdate = () => {
    const queryClient = useQueryClient();
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [uploadResult, setUploadResult] = useState(null);
    const [showSuccessDetails, setShowSuccessDetails] = useState(false);
    const [showFailureDetails, setShowFailureDetails] = useState(false);
    const [, setShowWarnings] = useState(false);

    // Filters
    const [locations, setLocations] = useState([]);
    const [branches, setBranches] = useState([]);
    const [managers, setManagers] = useState([]);
    const [teamHeads, setTeamHeads] = useState([]);

    const [selectedLocation, setSelectedLocation] = useState('');
    const [selectedBranch, setSelectedBranch] = useState('');
    const [selectedManager, setSelectedManager] = useState('');
    const [selectedTeamHead, setSelectedTeamHead] = useState('');

    // Fetch filters on mount
    useEffect(() => {
        const fetchFilters = async () => {
            try {
                const data = await getSalaryFilterOptions();
                setLocations(data.locations || []);
                setBranches(data.branches || []);
                setManagers(data.managers || []);
                setTeamHeads(data.team_heads || []);
            } catch (err) {
                console.error("Failed to load filters", err);
                toast.error("Failed to load filter options");
            }
        };
        fetchFilters();
    }, []);

    // Suppress ResizeObserver errors
    useEffect(() => {
        const resizeObserverErrHandler = (e) => {
            if (e.message && e.message.includes('ResizeObserver loop')) {
                const resizeObserverErr = e;
                resizeObserverErr.stopImmediatePropagation();
                return false;
            }
        };
        window.addEventListener('error', resizeObserverErrHandler);
        return () => window.removeEventListener('error', resizeObserverErrHandler);
    }, []);

    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0];
        if (selectedFile) {
            const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
            if (!['xlsx', 'xls'].includes(fileExtension)) {
                toast.error('Please select an Excel file (.xlsx or .xls)');
                return;
            }
            setFile(selectedFile);
            setUploadResult(null);
            toast.info(`Selected: ${selectedFile.name}`);
        }
    };

    const handleDownloadTemplate = async (includeData = false) => {
        try {
            setLoading(true);
            await downloadSalaryTemplate(includeData, '', {
                location: selectedLocation,
                branch: selectedBranch,
                manager: selectedManager,
                team_head: selectedTeamHead,
            }, 'salary');
            toast.success('Template downloaded successfully!');
        } catch (error) {
            toast.error('Failed to download template');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleUpload = async () => {
        if (!file) {
            toast.warning('Please select a file first');
            return;
        }

        try {
            setLoading(true);
            const response = await uploadBulkSalaryUpdate(file, {
                location: selectedLocation,
                branch: selectedBranch,
                manager: selectedManager,
                team_head: selectedTeamHead,
            });

            const result = response.results || response;
            setUploadResult(result);

            // Salary and employee PF flags changed for an arbitrary set of
            // employees. Clear every cached employee detail/preview query so
            // Salary View fetches the values just saved by this upload.
            if (result.successful > 0) {
                queryClient.invalidateQueries({ queryKey: ['singleEmployee'] });
            }

            if (result.failed === 0) {
                toast.success(`✅ Successfully updated ${result.successful} employees!`);
                setShowSuccessDetails(false); // No need for details if all success in simple list
            } else if (result.successful > 0) {
                toast.warning(
                    `⚠️ Partially completed: ${result.successful} succeeded, ${result.failed} failed`
                );
                setShowFailureDetails(true);
            } else {
                toast.error('❌ No employees were updated. Please check errors below.');
                setShowFailureDetails(true);
            }
        } catch (error) {
            toast.error(error.message || 'Upload failed');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setFile(null);
        setUploadResult(null);
        setShowSuccessDetails(false);
        setShowFailureDetails(false);
        setShowWarnings(false);
        const fileInput = document.getElementById('salary-file-upload');
        if (fileInput) fileInput.value = '';
    };

    const clearFilters = () => {
        setSelectedLocation('');
        setSelectedBranch('');
        setSelectedManager('');
        setSelectedTeamHead('');
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 } }}>
            {/* Header card */}
            <Paper elevation={0} sx={{ ...CARD_SX, mb: { xs: 2, md: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
                    <Box sx={{ width: 52, height: 52, borderRadius: '16px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <CloudUpload />
                    </Box>
                    <Box>
                        <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.35rem', md: '1.7rem' }, color: '#111827', letterSpacing: '-0.02em', lineHeight: 1.15 }}>
                            Bulk Salary Update
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#6b7280' }}>
                            Upload an Excel file to update employee salary details in bulk. Filter by Region/Branch for targeted updates.
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            {/* Filters Section */}
            <Paper elevation={0} sx={{ ...CARD_SX, mb: { xs: 2, md: 3 } }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 800, color: '#111827' }}>
                    <FilterList sx={{ color: ACCENT }} /> Filters
                </Typography>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Region / Location</InputLabel>
                            <Select
                                value={selectedLocation}
                                label="Region / Location"
                                onChange={(e) => setSelectedLocation(e.target.value)}
                                MenuProps={SELECTED_MENU_PROPS}
                            >
                                <MenuItem value=""><em>All Locations</em></MenuItem>
                                {locations.map((loc) => (
                                    <MenuItem key={loc} value={loc}>{loc}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Branch</InputLabel>
                            <Select
                                value={selectedBranch}
                                label="Branch"
                                onChange={(e) => setSelectedBranch(e.target.value)}
                                MenuProps={SELECTED_MENU_PROPS}
                            >
                                <MenuItem value=""><em>All Branches</em></MenuItem>
                                {branches.map((branch) => (
                                    <MenuItem key={branch} value={branch}>{branch}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Manager</InputLabel>
                            <Select
                                value={selectedManager}
                                label="Manager"
                                onChange={(e) => setSelectedManager(e.target.value)}
                                MenuProps={SELECTED_MENU_PROPS}
                            >
                                <MenuItem value=""><em>All Managers</em></MenuItem>
                                {managers.map((mgr) => (
                                    <MenuItem key={mgr} value={mgr}>{mgr}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Team Head</InputLabel>
                            <Select
                                value={selectedTeamHead}
                                label="Team Head"
                                onChange={(e) => setSelectedTeamHead(e.target.value)}
                                MenuProps={SELECTED_MENU_PROPS}
                            >
                                <MenuItem value=""><em>All Team Heads</em></MenuItem>
                                {teamHeads.map((head) => (
                                    <MenuItem key={head} value={head}>{head}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Button variant="text" onClick={clearFilters} disabled={!selectedLocation && !selectedBranch && !selectedManager && !selectedTeamHead} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '12px', color: ACCENT }}>
                            Clear Filters
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Template Download Section */}
            <Paper elevation={0} sx={{ ...CARD_SX, mb: { xs: 2, md: 3 } }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 800, color: '#111827' }}>
                    <Download sx={{ color: ACCENT }} /> Step 1: Download Template
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280', mb: 2 }}>
                    Download the template. If filters are selected above, only employees matching those filters will be included in the "Template with Current Data".
                </Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap">
                    <Button
                        variant="contained"
                        disableElevation
                        startIcon={<Download />}
                        onClick={() => handleDownloadTemplate(false)}
                        disabled={loading}
                        sx={{ ...GRADIENT_BTN, minWidth: 180 }}
                    >
                        Empty Template
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<Download />}
                        onClick={() => handleDownloadTemplate(true)}
                        disabled={loading}
                        sx={{ ...OUTLINED_BTN, minWidth: 220 }}
                    >
                        Template with Current Data
                    </Button>
                </Stack>
                <Alert severity="info" sx={{ mt: 2, borderRadius: '14px' }}>
                    <AlertTitle>Template Format</AlertTitle>
                    The upload expects 3 columns: <strong>Employee No</strong>, <strong>Monthly CTC</strong>, and <strong>PF</strong> (True/False).
                </Alert>
            </Paper>

            {/* File Upload Section */}
            <Paper elevation={0} sx={CARD_SX}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 800, color: '#111827' }}>
                    <CloudUpload sx={{ color: ACCENT }} /> Step 2: Upload Filled Template
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280', mb: 2 }}>
                    Select the completed Excel file.
                    {selectedLocation || selectedBranch ? (
                        <strong style={{ color: '#ef4444' }}> CAUTION: You have applied filters ({selectedLocation} {selectedBranch}). Only employees belonging to this selection can be updated. Others will be rejected.</strong>
                    ) : (
                        " Ensure employee numbers are correct."
                    )}
                </Typography>

                <Stack spacing={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <input
                            accept=".xlsx,.xls"
                            style={{ display: 'none' }}
                            id="salary-file-upload"
                            type="file"
                            onChange={handleFileChange}
                        />
                        <label htmlFor="salary-file-upload">
                            <Button
                                variant="outlined"
                                component="span"
                                startIcon={<AttachFile />}
                                disabled={loading}
                                sx={OUTLINED_BTN}
                            >
                                Choose File
                            </Button>
                        </label>
                        {file && (
                            <Chip
                                label={file.name}
                                onDelete={handleReset}
                                sx={{ fontWeight: 700, bgcolor: `${ACCENT}14`, color: ACCENT, border: 'none' }}
                            />
                        )}
                    </Box>

                    {file && (
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button
                                variant="contained"
                                disableElevation
                                onClick={handleUpload}
                                disabled={loading}
                                startIcon={<UploadFile />}
                                sx={{ ...GRADIENT_BTN, minWidth: 200 }}
                            >
                                {loading ? 'Processing...' : 'Upload & Process'}
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={handleReset}
                                disabled={loading}
                                sx={OUTLINED_BTN}
                            >
                                Cancel
                            </Button>
                        </Box>
                    )}
                </Stack>

                {loading && (
                    <Box sx={{ mt: 2 }}>
                        <LinearProgress sx={{ borderRadius: '14px' }} />
                        <Typography variant="caption" sx={{ color: '#6b7280', mt: 1, display: 'block' }}>
                            Processing salary updates... This may take a few moments.
                        </Typography>
                    </Box>
                )}
            </Paper>

            {/* Results Section */}
            {uploadResult && (
                <Paper elevation={0} sx={{ ...CARD_SX, mt: { xs: 2, md: 3 } }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 800, color: '#111827' }}>
                        Upload Results
                    </Typography>

                    {/* Summary */}
                    <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap' }} useFlexGap>
                        <ResultTile
                            icon={<CheckCircle />}
                            label="Successful"
                            value={uploadResult.successful || 0}
                            color="#16a34a"
                        />
                        <ResultTile
                            icon={<Error />}
                            label="Failed"
                            value={uploadResult.failed || 0}
                            color="#ef4444"
                        />
                    </Stack>

                    {/* Success Details */}
                    {uploadResult.successful_updates && uploadResult.successful_updates.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Button
                                onClick={() => setShowSuccessDetails(!showSuccessDetails)}
                                endIcon={showSuccessDetails ? <ExpandLess /> : <ExpandMore />}
                                size="small"
                                variant="outlined"
                                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '12px', color: '#16a34a', borderColor: '#16a34a55' }}
                            >
                                Successful Updates ({uploadResult.successful_updates.length})
                            </Button>
                            <Collapse in={showSuccessDetails} timeout="auto">
                                <TableContainer component={Paper} sx={{ ...TABLE_PAPER_SX, borderRadius: '22px', mt: 2, maxHeight: 400 }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={HEAD_CELL_SX}>Row #</TableCell>
                                                <TableCell sx={HEAD_CELL_SX}>Employee No</TableCell>
                                                <TableCell sx={HEAD_CELL_SX}>Name</TableCell>
                                                <TableCell sx={HEAD_CELL_SX}>Info</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {uploadResult.successful_updates.map((update, idx) => (
                                                <TableRow key={idx} sx={TABLE_ROW_SX}>
                                                    <TableCell sx={BODY_CELL_SX}>{update.row}</TableCell>
                                                    <TableCell sx={BODY_CELL_SX}>{update.employee_no}</TableCell>
                                                    <TableCell sx={BODY_CELL_SX}>{update.name}</TableCell>
                                                    <TableCell sx={BODY_CELL_SX}>
                                                        <Chip
                                                            label="Updated"
                                                            size="small"
                                                            sx={{ fontWeight: 700, bgcolor: '#f0fdf4', color: '#16a34a', border: 'none' }}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Collapse>
                        </Box>
                    )}

                    {/* Failure Details */}
                    {uploadResult.errors && uploadResult.errors.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Button
                                onClick={() => setShowFailureDetails(!showFailureDetails)}
                                endIcon={showFailureDetails ? <ExpandLess /> : <ExpandMore />}
                                size="small"
                                variant="outlined"
                                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '12px', color: '#ef4444', borderColor: '#ef444455' }}
                            >
                                Error Details ({uploadResult.errors.length})
                            </Button>
                            <Collapse in={showFailureDetails} timeout="auto">
                                <TableContainer component={Paper} sx={{ ...TABLE_PAPER_SX, borderRadius: '22px', mt: 2, maxHeight: 400 }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={HEAD_CELL_SX}>Row #</TableCell>
                                                <TableCell sx={HEAD_CELL_SX}>Employee No</TableCell>
                                                <TableCell sx={HEAD_CELL_SX}>Error</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {uploadResult.errors.map((err, idx) => (
                                                <TableRow key={idx} sx={TABLE_ROW_SX}>
                                                    <TableCell sx={BODY_CELL_SX}>{err.row}</TableCell>
                                                    <TableCell sx={BODY_CELL_SX}>{err.emp_id || 'N/A'}</TableCell>
                                                    <TableCell sx={BODY_CELL_SX}>
                                                        <Typography variant="body2" sx={{ color: '#ef4444' }}>
                                                            {err.error}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Collapse>
                        </Box>
                    )}

                    {uploadResult.successful > 0 && (
                        <Alert severity="success" sx={{ mt: 3, borderRadius: '14px' }}>
                            <AlertTitle>Success!</AlertTitle>
                            {uploadResult.successful} employee salary records have been updated successfully.
                        </Alert>
                    )}
                </Paper>
            )}
        </Box>
    );
};

export default BulkSalaryUpdate;
